import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	DefaultMatterAdjustmentTenancyPrepaid,
	MatterAdjustmentTenancyPrepaid,
	MatterAdjustmentTenancyPrepaidCalculatedInterest,
} from '@models/matter';
import { Select } from '@ngxs/store';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { format, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { AdjustmentData } from '../adjustments.types';
import { ICON } from '@core/util/icon';
import { calculateTenancyPrepaid } from '../../../../shared/services/adjustments';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { map } from 'rxjs/operators';
import { NgForm } from '@angular/forms';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';

@UntilDestroy()
@Component({
	selector: 'app-tenancy-prepaid',
	templateUrl: './tenancy-prepaid-dialog.component.html',
	standalone: false,
})
export class TenancyPrepaidDialogComponent implements OnInit {
	submitted = false;
	adjustment: MatterAdjustmentTenancyPrepaid = cloneDeep(
		DefaultMatterAdjustmentTenancyPrepaid,
	);
	adjustmentDate: string;
	@Select(SelectedMatter.adjustmentDate) adjustmentDate$: Observable<string>;
	@SelectSnapshot(SelectedMatter.matterHST) hst: number;
	areaItems = [
		{
			label: 'Silent',
			value: 'silent',
		},
		{
			label: 'Specify',
			value: 'specify',
		},
	];

	@Select(SelectedMatter.closingDate) closingDate$: Observable<string>;
	creditAmount: number;
	formatItems = [
		{
			label: 'Itemized calculation',
			value: 'itemized',
		},
		{
			label: 'Final amount only',
			value: 'final',
		},
	];

	hstAmount: number;
	readonly ICON = ICON;
	interestAmount: MatterAdjustmentTenancyPrepaidCalculatedInterest[];
	interestItems = [
		{
			label: 'Yes',
			value: 'yes',
		},
		{
			label: 'No',
			value: 'no',
		},
	];

	interestLabel: string;
	interestTotal: number;
	interestTypeItems = [
		{
			label: 'Flat rate',
			value: 'flat',
		},
		{
			label: 'Ontario residential tenancies act, 2006',
			value: 'act',
		},
	];

	@SelectSnapshot(
		AdjustmentDefaultDetailsSelectors.adjustmentDetailsTenancyPrepaid,
	)
	public adjustmentDetails: boolean;

	@Select(AdjustmentsSelector.tenancyPrepaidAdjustment)
	tenancyPrepaidAdjustmentFilterFn$: Observable<
		(id: string) => MatterAdjustmentTenancyPrepaid
	>;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: AdjustmentData,
		private dialogRef: MatDialogRef<TenancyPrepaidDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		this.adjustment = cloneDeep({
			...DefaultMatterAdjustmentTenancyPrepaid,
			details: this.adjustmentDetails,
		});
		this.creditAmount = 0;
		this.adjustmentDate$ // this order is important, `adjustmentDate` should override `closingDate` if it exists
			.pipe(untilDestroyed(this))
			.subscribe((date) => {
				this.adjustmentDate = date;
				this.changed();
			});

		this.getTenancyPrepaid();
	}

	getTenancyPrepaid = () => {
		this.tenancyPrepaidAdjustmentFilterFn$
			.pipe(
				untilDestroyed(this),
				map((fn) => fn(this.data.adjustmentId)),
			)
			.subscribe((_adj) => {
				if (_adj) {
					this.adjustment = cloneDeep(_adj);
					this.changed();
				}
			});
	};

	cancelModal(): void {
		this.dialogRef.close();
	}

	changed(): void {
		const prepaidCalculations = calculateTenancyPrepaid(
			this.adjustment.rent,
			this.adjustment.applyHst,
			this.adjustmentDate,
			this.adjustment.commencementDate,
			this.adjustment.interest,
			this.adjustment.interestRate,
			this.adjustment.interestType,
			this.hst,
		);

		if (
			this.adjustment?.commencementDate &&
			prepaidCalculations?.endDate &&
			prepaidCalculations?.daysBetween
		) {
			if (this.adjustment.interestType === 'flat') {
				this.interestLabel = `Interest at ${this.adjustment.interestRate}% from ${format(
					parseISO(this.adjustment.commencementDate),
					'PPP',
				)} to ${format(parseISO(prepaidCalculations.endDate), 'PPP')} (${prepaidCalculations.daysBetween} days)`;
			} else {
				this.interestLabel = `Accrued interest from ${format(parseISO(this.adjustment.commencementDate), 'PPP')} to ${format(
					parseISO(prepaidCalculations.endDate),
					'PPP',
				)} (${prepaidCalculations.daysBetween} days)`;
			}
		}

		this.creditAmount = prepaidCalculations.creditAmount;
		this.hstAmount = prepaidCalculations.hstAmount;
		this.interestAmount = prepaidCalculations.interestAmount;
		this.interestTotal = prepaidCalculations.interestTotal;
	}

	interestCompoundIndex(
		_,
		interest: MatterAdjustmentTenancyPrepaidCalculatedInterest,
	): string {
		return `${interest.days}${interest.amount}${interest.year}`;
	}

	submit(form: NgForm): void {
		this.submitted = true;
		if (form.valid) {
			this.adjustmentDispatch.Set(this.adjustment);
			this.dialogRef.close();
		}
	}
}
